import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import axiosInstance from "../utils/axios";
import { login } from "../utils/auth";
import { API_ENDPOINTS } from "../config";

const gradeOptions = ["1","2","3","4","5","6","7","8","9","10","11","12","College Student"];

// ─── Small reusable components ────────────────────────────────────────────────

const Spinner = () => (
  <svg className="animate-spin h-5 w-5 text-current" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
  </svg>
);

const ErrorBox = ({ msg }) => msg ? (
  <div className="bg-red-50 border-l-4 border-red-500 p-3 rounded-md flex items-start gap-2 text-sm text-red-700">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5 shrink-0" viewBox="0 0 20 20" fill="currentColor">
      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
    </svg>
    {msg}
  </div>
) : null;

const SuccessBox = ({ msg }) => msg ? (
  <div className="bg-green-50 border-l-4 border-green-500 p-3 rounded-md flex items-start gap-2 text-sm text-green-700">
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mt-0.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
    {msg}
  </div>
) : null;

const roleLabel = (role) => {
  const map = { student: "Student", teacher: "Teacher", "school admin": "School Admin", "publisher admin": "Publisher Admin", admin: "Admin" };
  return map[role] || role;
};

const avatarBg = (name) => {
  const colours = ["bg-blue-500","bg-purple-500","bg-green-500","bg-yellow-500","bg-pink-500","bg-indigo-500","bg-red-500","bg-teal-500"];
  let hash = 0;
  for (let i = 0; i < (name || "?").length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colours[Math.abs(hash) % colours.length];
};

// ─── Main component ───────────────────────────────────────────────────────────

const GoogleAccountSelect = () => {
  const [searchParams] = useSearchParams();

  const [googleToken, setGoogleToken] = useState("");
  const [email, setEmail] = useState("");
  const [accounts, setAccounts] = useState([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState("");

  // view: 'list' | 'login' | 'register'
  const [view, setView] = useState("list");

  // Login sub-state
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");

  // Register sub-state
  const [registerForm, setRegisterForm] = useState({
    fullname: "", username: "", phone: "", role: "", grade: "1", password: "", confirmPassword: ""
  });
  const [usernameError, setUsernameError] = useState("");
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState("");
  const [registerSuccess, setRegisterSuccess] = useState("");
  const [showRegPassword, setShowRegPassword] = useState(false);

  // Publisher auto-detection (mirrors Signup.js logic)
  const [publisher] = useState(() => {
    const h = window.location.hostname;
    return h === "chatbot-backend-v-4.onrender.com" || h.includes("chatbot-backend-v-4.onrender.com")
      ? "CP"
      : "EXCELLENCE PUBLICATION";
  });

  // ── Fetch accounts ───────────────────────────────────────────────────────────
  const fetchAccounts = useCallback(async (token) => {
    setPageLoading(true);
    setPageError("");
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.ACCOUNTS_BY_GOOGLE_TOKEN, { googleToken: token });
      setEmail(res.data.email || "");
      setAccounts(res.data.users || []);
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to load accounts.";
      setPageError(msg);
    } finally {
      setPageLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = searchParams.get("googleToken");
    if (!token) {
      setPageError("Invalid access. Please sign in with Google.");
      setPageLoading(false);
      return;
    }
    setGoogleToken(token);
    fetchAccounts(token);
  }, [searchParams, fetchAccounts]);

  // ── Login handlers ────────────────────────────────────────────────────────────
  const handleSelectUser = (account) => {
    setSelectedAccount(account);
    setPassword("");
    setLoginError("");
    setShowPassword(false);
    setView("login");
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    setLoginLoading(true);
    try {
      await login({ username: selectedAccount.username, password });
      window.location.href = "/chat";
    } catch (err) {
      setLoginError(err.response?.data?.message || "Incorrect password. Please try again.");
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Register handlers ─────────────────────────────────────────────────────────
  const handleRegisterChange = (e) => {
    const { name, value } = e.target;
    setRegisterForm(prev => ({ ...prev, [name]: value }));
    setRegisterError("");
    if (name === "username") setUsernameError("");
  };

  const checkUsername = async (username) => {
    if (!username?.trim()) { setUsernameError(""); return; }
    setCheckingUsername(true);
    try {
      const res = await fetch(API_ENDPOINTS.CHECK_USERNAME, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: username.trim() })
      });
      const data = await res.json();
      setUsernameError(res.status === 409 ? data.message : "");
    } catch {
      setUsernameError("Error checking username availability");
    } finally {
      setCheckingUsername(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegisterError("");
    setRegisterSuccess("");
    if (!registerForm.phone || !registerForm.phone.trim()) {
      setRegisterError("Phone number is required.");
      return;
    }
    if (registerForm.password !== registerForm.confirmPassword) {
      setRegisterError("Passwords do not match.");
      return;
    }
    if (usernameError) {
      setRegisterError("Please choose a different username.");
      return;
    }
    setRegisterLoading(true);
    try {
      const res = await axiosInstance.post(API_ENDPOINTS.REGISTER_GOOGLE_VERIFIED, {
        googleToken,
        username: registerForm.username.trim(),
        fullname: registerForm.fullname.trim(),
        phone: registerForm.phone.trim(),
        role: registerForm.role.trim(),
        grade: registerForm.grade,
        password: registerForm.password.trim(),
        publisher
      });
      setRegisterSuccess(res.data.message || "Account created successfully!");
      await fetchAccounts(googleToken);
      setTimeout(() => {
        setView("list");
        setRegisterSuccess("");
        setRegisterForm({ fullname: "", username: "", phone: "", role: "", grade: "1", password: "", confirmPassword: "" });
        setUsernameError("");
      }, 2000);
    } catch (err) {
      setRegisterError(err.response?.data?.message || "Failed to create account. Please try again.");
    } finally {
      setRegisterLoading(false);
    }
  };

  // ── Page-level loading / error ────────────────────────────────────────────────
  if (pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50">
        <div className="flex flex-col items-center gap-3 text-gray-500">
          <Spinner />
          <p className="text-sm">Loading your accounts…</p>
        </div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50 px-4">
        <div className="max-w-sm w-full bg-white rounded-xl shadow-lg p-8 text-center space-y-4">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-red-400 mx-auto" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
          </svg>
          <p className="text-gray-700 font-medium">{pageError}</p>
          <a href="/login" className="inline-block text-sm text-blue-600 hover:text-blue-500 font-medium">← Back to login</a>
        </div>
      </div>
    );
  }

  // ── LOGIN sub-view ────────────────────────────────────────────────────────────
  if (view === "login" && selectedAccount) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50 py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-100 p-8 space-y-6">
          {/* Header */}
          <div className="text-center">
            <div className={`w-16 h-16 rounded-full ${avatarBg(selectedAccount.fullname)} flex items-center justify-center text-white text-2xl font-bold mx-auto`}>
              {selectedAccount.fullname?.[0]?.toUpperCase() || "?"}
            </div>
            <h2 className="mt-3 text-2xl font-bold text-gray-900">{selectedAccount.fullname}</h2>
            <p className="text-sm text-gray-500">@{selectedAccount.username}</p>
            <span className="mt-1 inline-block text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">{roleLabel(selectedAccount.role)}</span>
          </div>

          <ErrorBox msg={loginError} />

          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  required
                  autoFocus
                  autoComplete="current-password"
                  className="appearance-none rounded-lg block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Enter your password"
                  value={password}
                  onChange={e => { setPassword(e.target.value); setLoginError(""); }}
                />
                <button type="button" tabIndex={-1} onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                  {showPassword
                    ? <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" /></svg>
                    : <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  }
                </button>
              </div>
            </div>
            <button type="submit" disabled={loginLoading}
              className={`w-full flex justify-center items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium text-white ${loginLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} transition-colors`}>
              {loginLoading ? <><Spinner /> Signing in…</> : "Sign in"}
            </button>
          </form>

          <div className="text-center space-y-2">
            <a
              href={`/forgot-password?username=${encodeURIComponent(selectedAccount.username)}`}
              className="text-sm text-blue-600 hover:text-blue-500"
            >
              Forgot password?
            </a>
            <br />
            <button type="button" onClick={() => setView("list")} className="text-sm text-gray-500 hover:text-gray-700">
              ← Choose a different account
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── REGISTER sub-view ─────────────────────────────────────────────────────────
  if (view === "register") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50 py-12 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-lg border border-gray-100 p-8 space-y-5">
          {/* Header */}
          <div className="text-center">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
              </svg>
            </div>
            <h2 className="mt-3 text-xl font-bold text-gray-900">Create new account</h2>
            <p className="text-xs text-gray-500 mt-1 flex items-center justify-center gap-1">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-green-500" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              Email: <span className="font-medium text-gray-700">{email}</span>
            </p>
          </div>

          <ErrorBox msg={registerError} />
          <SuccessBox msg={registerSuccess} />

          <form onSubmit={handleRegister} className="space-y-3">
            {/* Full name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Full Name</label>
              <input name="fullname" type="text" required
                className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your full name" value={registerForm.fullname} onChange={handleRegisterChange} />
            </div>

            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username <span className="text-blue-600 font-medium text-xs">(used for login)</span>
              </label>
              <div className="relative">
                <input name="username" type="text" required
                  className={`appearance-none rounded-lg block w-full px-3 py-2 pr-8 border ${usernameError ? "border-red-400 focus:ring-red-500 focus:border-red-500" : "border-gray-300 focus:ring-blue-500 focus:border-blue-500"} placeholder-gray-400 text-gray-900 focus:outline-none sm:text-sm`}
                  placeholder="Choose a username" value={registerForm.username}
                  onChange={handleRegisterChange}
                  onBlur={e => checkUsername(e.target.value)} />
                {checkingUsername && <div className="absolute inset-y-0 right-2 flex items-center"><Spinner /></div>}
              </div>
              {usernameError && <p className="text-xs text-red-600 mt-0.5">{usernameError}</p>}
            </div>

            {/* Phone */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number</label>
              <input name="phone" type="tel" required
                className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Your phone number" value={registerForm.phone} onChange={handleRegisterChange} />
            </div>

            {/* Role */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
              <select name="role" required
                className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={registerForm.role} onChange={handleRegisterChange}>
                <option value="">Select your role</option>
                <option value="student">Student</option>
                <option value="teacher">Teacher</option>
              </select>
            </div>

            {/* Grade */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Grade Level</label>
              <select name="grade"
                className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                value={registerForm.grade} onChange={handleRegisterChange}>
                {gradeOptions.map(g => <option key={g} value={g}>Grade {g}</option>)}
              </select>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input name="password" type={showRegPassword ? "text" : "password"} required
                  className="appearance-none rounded-lg block w-full px-3 py-2 pr-10 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Create a password (min 6 chars)" value={registerForm.password} onChange={handleRegisterChange} />
                <button type="button" tabIndex={-1} onClick={() => setShowRegPassword(v => !v)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={showRegPassword ? "M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" : "M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"} />
                  </svg>
                </button>
              </div>
            </div>

            {/* Confirm password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password</label>
              <input name="confirmPassword" type="password" required
                className="appearance-none rounded-lg block w-full px-3 py-2 border border-gray-300 placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                placeholder="Confirm your password" value={registerForm.confirmPassword} onChange={handleRegisterChange} />
            </div>

            <button type="submit" disabled={registerLoading}
              className={`w-full flex justify-center items-center gap-2 py-2 px-4 rounded-lg text-sm font-medium text-white ${registerLoading ? "bg-blue-400 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"} transition-colors mt-2`}>
              {registerLoading ? <><Spinner /> Creating account…</> : "Create Account"}
            </button>
          </form>

          <div className="text-center">
            <button type="button" onClick={() => { setView("list"); setRegisterError(""); setRegisterSuccess(""); }}
              className="text-sm text-gray-500 hover:text-gray-700">
              ← Back to accounts
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── ACCOUNT LIST view ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-blue-50 to-gray-50 py-12 px-4">
      <div className="max-w-lg w-full space-y-6">
        {/* Header */}
        <div className="text-center">
          <div className="flex items-center justify-center gap-2 mb-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" viewBox="0 0 24 24" fill="currentColor">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            <h1 className="text-2xl font-bold text-gray-900">Choose an account</h1>
          </div>
          <p className="text-sm text-gray-500">
            Signed in as <span className="font-medium text-gray-700">{email}</span>
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden divide-y divide-gray-100">
          {/* Existing accounts */}
          {accounts.length === 0 && (
            <div className="px-6 py-4 text-sm text-gray-500 text-center">
              No existing accounts found for this email.
            </div>
          )}

          {accounts.map((account) => (
            <button
              key={account.username}
              type="button"
              onClick={() => handleSelectUser(account)}
              className="w-full flex items-center gap-4 px-6 py-4 hover:bg-blue-50 transition-colors text-left group"
            >
              {account.profilePicture ? (
                <img src={account.profilePicture} alt="" className="w-12 h-12 rounded-full object-cover shrink-0" />
              ) : (
                <div className={`w-12 h-12 rounded-full ${avatarBg(account.fullname)} flex items-center justify-center text-white text-xl font-bold shrink-0`}>
                  {account.fullname?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 group-hover:text-blue-700 truncate">{account.fullname}</p>
                <p className="text-sm text-gray-500 truncate">@{account.username}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{roleLabel(account.role)}</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 group-hover:text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </div>
            </button>
          ))}

          {/* Add new user */}
          <button
            type="button"
            onClick={() => { setView("register"); setRegisterError(""); setRegisterSuccess(""); }}
            className="w-full flex items-center gap-4 px-6 py-4 hover:bg-green-50 transition-colors text-left group"
          >
            <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center shrink-0">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <div className="flex-1">
              <p className="font-semibold text-green-700 group-hover:text-green-800">Add new account</p>
              <p className="text-sm text-gray-500">Create a new profile for {email}</p>
            </div>
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-gray-400 group-hover:text-green-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        </div>

        <p className="text-center text-xs text-gray-400">
          Not you?{" "}
          <a href="/login" className="text-blue-500 hover:text-blue-600 font-medium">Sign in with a different account</a>
        </p>
      </div>
    </div>
  );
};

export default GoogleAccountSelect;
